import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileText, Target, Heart, Zap, Users, ArrowRight } from 'lucide-react';
import styles from '../../styles/institutional.module.css';

const VALUES = [
    {
        icon: <Zap size={20} />,
        title: 'Simplicidade',
        desc: 'Acreditamos que ferramentas profissionais não precisam ser complicadas. Cada funcionalidade foi pensada para economizar seu tempo.',
        color: 'indigo',
    },
    {
        icon: <Heart size={20} />,
        title: 'Feito para brasileiros',
        desc: 'Desenvolvido do zero para atender as necessidades fiscais do Brasil: ISS, IRRF, CSLL, COFINS, PIS — tudo no padrão nacional.',
        color: 'green',
    },
    {
        icon: <Target size={20} />,
        title: 'Foco no freelancer',
        desc: 'Somos uma ferramenta criada por quem já foi freelancer. Sabemos o que é perder horas gerando nota de serviço manualmente.',
        color: 'purple',
    },
    {
        icon: <Users size={20} />,
        title: 'Crescendo com você',
        desc: 'Começamos com o básico e evoluímos com base no feedback real dos nossos usuários. Sua voz molda o produto.',
        color: 'orange',
    },
];

const TIMELINE = [
    { year: '2023', title: 'Ideia nasceu', desc: 'Frustração real: perder horas toda semana gerando orçamentos em planilhas e PDFs editáveis.' },
    { year: '2024', title: 'Primeiro protótipo', desc: 'Versão alpha lançada para um grupo fechado de freelancers de design e desenvolvimento.' },
    { year: '2024', title: 'Lançamento público', desc: 'Orçamento Digital abre para todos. Planos gratuito, Plus e Premium disponíveis desde o primeiro dia.' },
    { year: '2025', title: 'App Android', desc: 'Emita orçamentos diretamente pelo celular, em qualquer lugar, com o mesmo visual profissional.' },
];

export default function Sobre() {
    return (
        <div className={styles.page}>
            <Helmet>
                <title>Sobre — Orçamento Digital</title>
                <meta
                    name="description"
                    content="Conheça a história do Orçamento Digital, o sistema criado para simplificar a emissão de orçamentos e notas de serviço para autônomos e pequenas empresas."
                />
                <link rel="canonical" href="https://www.orcamento-digital.com/sobre" />
            </Helmet>
            {/* ── Hero ── */}
            <section className={styles.hero}>
                <div className={styles.heroGlow} />
                <div className={styles.heroContent}>
                    <div className={styles.logoMark}>
                        <FileText size={28} className={styles.logoIcon} />
                        <span>Orçamento Digital</span>
                    </div>
                    <h1 className={styles.heroTitle}>
                        Nascemos para simplificar a <span className={styles.accent}>burocracia fiscal</span> do freelancer brasileiro.
                    </h1>
                    <p className={styles.heroDesc}>
                        O Orçamento Digital é uma plataforma de emissão de orçamentos desenvolvida
                        com foco total em quem trabalha de forma independente — designers, devs,
                        consultores, fotógrafos e muito mais.
                    </p>
                </div>
            </section>

            {/* ── Missão ── */}
            <section className={styles.section}>
                <div className={styles.missionBox}>
                    <p className={styles.missionLabel}>Nossa missão</p>
                    <h2 className={styles.missionText}>
                        "Tornar a emissão de notas de serviço tão simples quanto enviar uma mensagem."
                    </h2>
                </div>
            </section>

            {/* ── Valores ── */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>O que nos guia</h2>
                </div>
                <div className={styles.valuesGrid}>
                    {VALUES.map((v, i) => (
                        <div key={i} className={`${styles.valueCard} ${styles[`vc_${v.color}`]}`}>
                            <div className={styles.valueIcon}>{v.icon}</div>
                            <h3 className={styles.valueTitle}>{v.title}</h3>
                            <p className={styles.valueDesc}>{v.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Timeline ── */}
            <section className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Nossa história</h2>
                </div>
                <div className={styles.timeline}>
                    {TIMELINE.map((item, i) => (
                        <div key={i} className={styles.timelineItem}>
                            <div className={styles.timelineDot} />
                            <div className={styles.timelineYear}>{item.year}</div>
                            <div className={styles.timelineContent}>
                                <h3 className={styles.timelineTitle}>{item.title}</h3>
                                <p className={styles.timelineDesc}>{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── CTA ── */}
            <section className={styles.ctaSection}>
                <div className={styles.ctaBox}>
                    <h2 className={styles.ctaTitle}>Pronto para simplificar sua gestão?</h2>
                    <p className={styles.ctaDesc}>Junte-se a centenas de freelancers que já usam o Orçamento Digital.</p>
                    <Link to="/register" className={styles.ctaBtn}>
                        Criar conta grátis <ArrowRight size={16} />
                    </Link>
                </div>
            </section>

        </div>
    );
}